import type { GameState, PendingEffect, PlayerState, EffectOp, StatusInstance, PickupResume } from "@/types/game";
import type { ResponseKind, Suit, CardKind } from "@/types/cards";
import {
  beginTurn,
  performDraw,
  enterDiscardPhase,
  performDiscard,
  endTurn,
} from "./TurnMachine";
import {
  openResponseWindow,
  setResponseDecision,
  allResponded,
  resolveWindow,
  makeReflectedEffect,
  makePendingEffect,
  canRespondWith,
} from "./Resolver";
import { computeDamage, applyDamage, applyHeal, attackCardDamage } from "./damage";
import { markDying, rescueByHeal, finalizeCircleEnd } from "./dying";
import { pushEvent } from "./Events";
import { canPickFromDiscard } from "./Deck";
import {
  equip as doEquip,
  equipSlotOf,
  unequip as doUnequip,
  effectiveHandLimit,
  equipManaCostReduction,
  isSlotProtectedByJingang,
} from "./Equipment";
import {
  parseSupplyHeal,
  parseSoulAmount,
  parseSkillDamage,
} from "./effectParse";
import { inRange, baseDistance } from "./distance";
import {
  createStatus,
  addStatusWithEvent,
  isFrozen,
  isSilenced,
  isEffectLocked,
  isAttackLocked,
  hasBloodlust,
  fateLinkPartner,
  isNoHeal,
} from "./Status";
import { getPassive, getActiveSkill } from "./skills/registry";
import { Rng } from "@/utils/rng";
import type { CardCatalogEntry, DeckCard } from "@/types/cards";
import { openPickupWindow, resolvePickupDecision } from "./Pickup";

export type EquipSlot = "weapon" | "armor" | "accessory";

// 引擎动作：apply() 返回新 state，不就地 mutate
export type EngineAction =
  | { type: "start-game" }
  | { type: "begin-turn" }
  | { type: "draw" }
  | { type: "end-turn" }
  | { type: "discard"; uids: number[] }
  | {
      type: "play-card";
      playerId: string;
      cardUid: number;
      substituteCardUids?: [number, number]; // 替换模式：消耗的2张手牌（弃鬼盾/掠夺之甲/杀戮之戒/狂风项链）
      substituteAs?: string; // 虚拟牌名："掠夺"/"PG攻击"/"HG攻击"
      targetId?: string;
      targetId2?: string; // 呼神项链：攻击的第二个目标
      skillName?: string;
      slot?: EquipSlot; // 缴械/掠夺：指定装备槽
      suit?: Suit; // 杀戮之心：指定花色
      takeFromDiscardUid?: number; // 拾遗：指定弃牌堆中的牌
      stealCardUid?: number; // 飞龙探云手：抽取目标手牌中指定位置的牌（玩家背面盲选）
    }
  | { type: "respond"; playerId: string; cardUid: number; responseKind: ResponseKind; substituteCardUids?: [number, number] }
  | { type: "pass-response"; playerId: string }
  | { type: "pickup"; playerId: string; cardUid: number; targetUid: number } // 拾遗窗口：取走弃牌堆中一张
  | { type: "pass-pickup"; playerId: string }; // 拾遗窗口：放弃

export function apply(state: GameState, action: EngineAction): GameState {
  switch (action.type) {
    case "start-game":
      return startGame(state);
    case "begin-turn":
      return beginTurn(state, state.currentPlayerIdx);
    case "draw":
      return performDraw(state);
    case "end-turn":
      return handleEndTurn(state);
    case "discard":
      return performDiscard(state, action.uids);
    case "play-card":
      return playCard(state, action);
    case "respond":
      return handleRespond(state, action.playerId, action.cardUid, action.responseKind, action.substituteCardUids);
    case "pass-response":
      return handleRespond(state, action.playerId, null, null);
    case "pickup":
      return handlePickup(state, action.playerId, action.cardUid, action.targetUid);
    case "pass-pickup":
      return handlePickup(state, action.playerId, null, null);
    default: {
      const _: never = action;
      throw new Error(`未知动作: ${JSON.stringify(_)}`);
    }
  }
}

function startGame(state: GameState): GameState {
  const cur = state.currentPlayerIdx;
  const withBegin = beginTurn(state, cur);
  return pushEvent(withBegin, {
    type: "turn-start",
    playerId: state.players[cur].id,
    round: state.round,
  });
}

function handleEndTurn(state: GameState): GameState {
  const cur = state.players[state.currentPlayerIdx];
  const passive = getPassive(cur.hero.name);
  // 圣殿骑士暗兵：执行阶段没出牌 → 可跳过弃牌（直接结束回合）
  const skipDiscard =
    state.phase === "play" &&
    passive.skipDiscardIfNoPlay === true &&
    !state.turnFlags.anyCardPlayed; // 暗兵：整回合未打出任何牌才可跳过弃牌
  // play 阶段且手牌超限：先进入弃牌阶段，等待玩家/AI 显式选择弃牌后再结束
  if (
    state.phase === "play" &&
    !skipDiscard &&
    cur.hand.length > effectiveHandLimit(cur)
  ) {
    return enterDiscardPhase(state);
  }
  const ended = endTurn(state);
  // 一圈环绕（round+1）时结算濒死/死亡/胜负
  if (ended.round > state.round) {
    return finalizeCircleEnd(ended);
  }
  return ended;
}

function handleRespond(
  state: GameState,
  playerId: string,
  cardUid: number | null,
  kind: ResponseKind | null,
  substituteCardUids?: [number, number]
): GameState {
  if (!state.responseWindow) return state;
  const next =
    cardUid === null || kind === null
      ? setResponseDecision(state, playerId, "pass")
      : substituteCardUids
        ? makeSubstituteResponse(state, playerId, kind, substituteCardUids)
        : makeCardResponse(state, playerId, cardUid, kind);
  if (allResponded(next)) {
    return finalizeWindow(next);
  }
  return next;
}

function makeCardResponse(
  state: GameState,
  playerId: string,
  cardUid: number,
  kind: ResponseKind
): GameState {
  const player = state.players.find((p) => p.id === playerId)!;
  const card = player.hand.find((c) => c.uid === cardUid);
  if (!card) throw new Error(`玩家 ${playerId} 手中没有牌 uid=${cardUid}`);
  if (!canRespondWith(state, playerId, kind)) {
    throw new Error(`玩家 ${playerId} 当前不能以【${kind}】响应`);
  }
  return setResponseDecision(state, playerId, { card, kind });
}

// 替换响应（弃鬼盾→解除 / 狂风刃甲→反射）：消耗2张手牌，构造虚拟牌
function makeSubstituteResponse(
  state: GameState,
  playerId: string,
  kind: ResponseKind,
  uids: [number, number]
): GameState {
  const player = state.players.find((p) => p.id === playerId)!;
  const sub = findSubstituteConfig(player, "respond");
  if (!sub) throw new Error("没有可用于响应替换的装备");
  if (sub.config.cardName !== kind) {
    throw new Error(`装备 ${sub.equipName} 替换为 ${sub.config.cardName}，而非 ${kind}`);
  }
  if (!canRespondWith(state, playerId, kind)) {
    throw new Error(`玩家 ${playerId} 当前不能以【${kind}】响应`);
  }
  const { state: spentState, cards } = spendSubstituteCards(state, playerId, uids);
  validateSubstituteSuit(cards, sub.config.suitFilter);
  const virtualCard = makeVirtualCard(spentState, kind);
  return setResponseDecision(spentState, playerId, { card: virtualCard, kind, virtual: true });
}

// 拾遗窗口决策
function handlePickup(
  state: GameState,
  playerId: string,
  cardUid: number | null,
  targetUid: number | null
): GameState {
  if (!state.pickupWindow) return state;
  // 判定场景：窗口关闭后须继续原效果流程（取消回执行阶段 / 开响应窗 / 直接结算）
  const resume = state.pickupWindow.resume;
  const resolved = resolvePickupDecision(state, playerId, cardUid, targetUid);
  if (resolved.pickupWindow || !resume) return resolved;
  return continueAfterJudgment(resolved, resume.effect, resume.cancelled);
}

// 本批被动弃牌/判定牌出现后，尝试打开拾遗窗口
function maybeOpenPickup(
  state: GameState,
  newCards: DeckCard[],
  victimId: string,
  reason: string,
  resume?: PickupResume
): GameState {
  if (newCards.length === 0) return state;
  // 有濒死玩家待救援时，救援优先，拾遗暂不开窗（牌留在弃牌堆，之后可主动拾遗）
  if (state.pendingDyingPlayers.length > 0) return state;
  return openPickupWindow(state, newCards, victimId, reason, resume);
}

// 响应窗口全员决策完毕后的结算
function finalizeWindow(state: GameState): GameState {
  const effect = state.responseWindow!.effect;
  const { state: afterResponses, outcome } = resolveWindow(state);

  if (outcome === "cancelled") {
    // 诛神之剑：攻击牌被闪避/无敌取消时，也算未造成伤害
    const src = afterResponses.players.find((p) => p.id === effect.source);
    if (
      effect.card?.kind === "攻击牌" &&
      src?.equip.weapon?.name === "诛神之剑" &&
      afterResponses.turnFlags.attacksPlayed === 1
    ) {
      return {
        ...afterResponses,
        turnFlags: { ...afterResponses.turnFlags, zhushenComboAvailable: true },
      };
    }
    return afterResponses; // 效果取消，回到 play
  }
  if (outcome === "reflect") {
    const reflected = makeReflectedEffect(afterResponses, effect);
    return openResponseWindow(afterResponses, reflected);
  }
  // 无响应：结算原效果
  return resolveEffect(afterResponses, effect);
}

// 结算效果：伤害 / 恢复 / 操作
function resolveEffect(state: GameState, effect: PendingEffect): GameState {
  const beforeUids = new Set(state.discardPile.map((c) => c.uid));
  const resolved = resolveEffectInner(state, effect);
  if (resolved === state || resolved.phase === "game-over") return resolved;
  // 结算中新增的被动弃牌/判定牌 → 开拾遗响应窗口（闪电/杀戮之心/缴械等）
  const newCards = resolved.discardPile.filter(
    (c) =>
      !beforeUids.has(c.uid) &&
      (c.discardReason === "passive" || c.discardReason === "judgment")
  );
  if (newCards.length === 0) return resolved;
  let reason = "被动弃牌";
  if (effect.op?.kind === "unequip") reason = "缴械";
  else if (effect.op?.kind === "discard-suit") reason = "杀戮之心";
  else if (effect.attackExtras?.includes("闪电")) reason = "闪电";
  const victimId = Array.isArray(effect.target)
    ? effect.target[0]
    : effect.target;
  return maybeOpenPickup(resolved, newCards, victimId, reason);
}

function resolveEffectInner(state: GameState, effect: PendingEffect): GameState {
  // 无伤害类：补给恢复 / 公共法术与效果牌操作
  if (!effect.damageType) {
    if (effect.healHp || effect.healMp) {
      return applySupplyHeal(state, effect);
    }
    if (effect.op) {
      return applyOp(state, effect);
    }
    return state;
  }
  const source = state.players.find((p) => p.id === effect.source)!;
  const targetIds = Array.isArray(effect.target) ? effect.target : [effect.target];
  let next = state;
  let lifeDrainTotal = 0; // 摄魂吸血：累计实际造成的 ss 伤害
  let totalDamageDealt = 0; // 诛神之剑：追踪攻击牌是否造成伤害

  // 主动技能附加状态先于伤害结算（巨剑之舞：本次伤害即享受无魔御）
  if (effect.skillStatuses && !effect.direct) {
    for (const spec of effect.skillStatuses) {
      for (const tid of targetIds) {
        const p = next.players.find((x) => x.id === tid)!;
        if (!p.isAlive && p.hp <= 0) continue;
        next = addStatusWithEvent(
          next,
          tid,
          createStatus(spec.name, effect.source, spec.turns, spec.modifiers)
        );
      }
    }
  }

  for (const tid of targetIds) {
    const target = next.players.find((p) => p.id === tid)!;
    if (!target.isAlive && target.hp <= 0) continue;

    // 反射反弹：伤害直结（已在上游算好最终值，不再扣减防御/魔御）
    if (effect.direct) {
      const res = {
        finalDamage: effect.damageAmount ?? 0,
        killable: effect.damageType !== "ss" && effect.damageType !== "mana",
      };
      const applied = applyDamage(target, res, effect.damageType!);
      next = updatePlayer(next, tid, (p) => ({
        ...p,
        hp: applied.hp,
        mp: applied.mp,
      }));
      next = pushEvent(next, {
        type: "damage",
        sourceId: effect.source,
        targetId: tid,
        amount: res.finalDamage,
        damageType: effect.damageType!,
      });
      if (applied.isDying) {
        next = markDying(next, tid);
      }
      if (effect.damageType === "ss") lifeDrainTotal += res.finalDamage;
      continue;
    }

    // 闪电：目标弃1张手牌否则双倍（P2 简化：有手牌自动弃1）
    let mult = effect.multiplier ?? 1;
    if (effect.attackExtras?.includes("闪电")) {
      if (target.hand.length > 0) {
        const drop = target.hand[target.hand.length - 1];
        next = updatePlayer(next, tid, (p) => ({
          ...p,
          hand: p.hand.filter((c) => c.uid !== drop.uid),
        }));
        // 被动弃牌：可被拾遗取回
        next = {
          ...next,
          discardPile: [...next.discardPile, { ...drop, discardReason: "passive" }],
        };
        next = pushEvent(next, { type: "discard", playerId: tid, count: 1 });
      } else {
        mult *= 2;
      }
    }

    // 命运链接：平摊伤害
    const partnerId = fateLinkPartner(target);
    const partner =
      partnerId && partnerId !== tid
        ? next.players.find((p) => p.id === partnerId)
        : undefined;
    const linked = !!(partner && partner.isAlive);

    // 灭焰魔甲：免疫10及以下的法术伤害（f/sf 基础伤害≤10 时为0）
    const spellImmune =
      target.equip.armor?.name === "灭焰魔甲" &&
      (effect.damageType === "f" || effect.damageType === "sf") &&
      (effect.damageAmount ?? 0) <= 10;

    const res = spellImmune
      ? {
          finalDamage: 0,
          killable:
            effect.damageType !== "ss" && effect.damageType !== "mana",
        }
      : computeDamage(
          effect.damageType!,
          effect.damageAmount ?? 0,
          source,
          target,
          mult,
          effect.ignoreDefense ?? 0
        );

    if (linked) {
      const halfA = Math.ceil(res.finalDamage / 2);
      const halfB = res.finalDamage - halfA;
      // ss/mana 各自独立结算（保留不可致死上限）
      const isCapType = effect.damageType === "ss" || effect.damageType === "mana";
      const resA = isCapType
        ? computeDamage(effect.damageType!, effect.damageAmount ?? 0, source, target, mult, effect.ignoreDefense ?? 0)
        : { ...res, finalDamage: halfA };
      const resB = isCapType
        ? computeDamage(effect.damageType!, effect.damageAmount ?? 0, source, partner, mult, effect.ignoreDefense ?? 0)
        : { ...res, finalDamage: halfB };
      const appliedA = applyDamage(target, resA, effect.damageType!);
      next = updatePlayer(next, tid, (p) => ({ ...p, hp: appliedA.hp, mp: appliedA.mp }));
      next = pushEvent(next, {
        type: "damage",
        sourceId: effect.source,
        targetId: tid,
        amount: resA.finalDamage,
        damageType: effect.damageType!,
      });
      if (appliedA.isDying) next = markDying(next, tid);
      const appliedB = applyDamage(partner, resB, effect.damageType!);
      next = updatePlayer(next, partner.id, (p) => ({ ...p, hp: appliedB.hp, mp: appliedB.mp }));
      next = pushEvent(next, {
        type: "damage",
        sourceId: effect.source,
        targetId: partner.id,
        amount: resB.finalDamage,
        damageType: effect.damageType!,
      });
      if (appliedB.isDying) next = markDying(next, partner.id);
      if (effect.damageType === "ss") lifeDrainTotal += resA.finalDamage + resB.finalDamage;
      totalDamageDealt += resA.finalDamage + resB.finalDamage;
    } else {
      const applied = applyDamage(target, res, effect.damageType!);
      next = updatePlayer(next, tid, (p) => ({
        ...p,
        hp: applied.hp,
        mp: applied.mp,
      }));
      next = pushEvent(next, {
        type: "damage",
        sourceId: effect.source,
        targetId: tid,
        amount: res.finalDamage,
        damageType: effect.damageType!,
      });
      if (applied.isDying) {
        next = markDying(next, tid);
      }
      if (effect.damageType === "ss") lifeDrainTotal += res.finalDamage;
      totalDamageDealt += res.finalDamage;
    }

    // 攻击牌命中后的附加效果（反射反弹不重触发）
    if (effect.card?.kind === "攻击牌") {
      next = applyAttackExtras(next, effect, source.id, tid);
      next = applyOnAttackHitSteal(next, source, target);
      // 恐惧之盾：若攻击造成伤害，目标拿起攻击牌
      next = applyFearShield(next, effect, tid, res.finalDamage);
    }
  }

  // 诛神之剑：攻击牌未造成伤害时，可再打出1张攻击牌
  // 仅在第一次攻击（attacksPlayed===1）触发，避免连击链
  if (
    !effect.direct &&
    effect.card?.kind === "攻击牌" &&
    totalDamageDealt === 0 &&
    source.equip.weapon?.name === "诛神之剑" &&
    next.turnFlags.attacksPlayed === 1
  ) {
    next = {
      ...next,
      turnFlags: { ...next.turnFlags, zhushenComboAvailable: true },
    };
  }

  // 摄魂吸血：来源恢复等量生命
  if (lifeDrainTotal > 0) {
    const src = next.players.find((p) => p.id === effect.source);
    if (src && src.isAlive) {
      next = applyHealWithLink(next, effect.source, lifeDrainTotal, 0);
      next = pushEvent(next, {
        type: "heal",
        sourceId: effect.source,
        targetId: effect.source,
        amount: lifeDrainTotal,
      });
    }
  }

  return next;
}

// TG攻击附加效果（命中后触发）
function applyAttackExtras(
  state: GameState,
  effect: PendingEffect,
  sourceId: string,
  targetId: string
): GameState {
  const extras = effect.attackExtras ?? [];
  let next = state;
  for (const ex of extras) {
    switch (ex) {
      case "烈焰": {
        // 额外对除目标以外的所有敌人造成 5 点损失生命（ss，不可致死）
        const source = next.players.find((p) => p.id === sourceId)!;
        for (const enemy of next.players.filter(
          (p) => p.team !== source.team && p.isAlive && p.id !== targetId
        )) {
          const dmg = Math.min(5, Math.max(0, enemy.hp - 1));
          if (dmg <= 0) continue;
          next = updatePlayer(next, enemy.id, (p) => ({ ...p, hp: p.hp - dmg }));
          next = pushEvent(next, {
            type: "damage",
            sourceId,
            targetId: enemy.id,
            amount: dmg,
            damageType: "ss",
          });
        }
        break;
      }
      case "寒冰":
        // 目标跳过本回合 + 回合外不能打出任何手牌
        next = addStatusWithEvent(
          next,
          targetId,
          createStatus("寒冰", sourceId, 1, { frozen: true })
        );
        break;
      case "剧毒": {
        // 本圈剩下的每个玩家回合前损失生命 2（持续整圈：永久状态 + 一圈结束移除）
        const st: StatusInstance = {
          ...createStatus("剧毒", sourceId, -1, { poison: 2 }),
          expiresOnCircleEnd: true,
        };
        next = addStatusWithEvent(next, targetId, st);
        break;
      }
      case "暴击追加": {
        // 暴击后追加 20 点损失生命（ss，不可致死）
        const victim = next.players.find((p) => p.id === targetId)!;
        const dmg = Math.min(20, Math.max(0, victim.hp - 1));
        if (dmg > 0) {
          next = updatePlayer(next, targetId, (p) => ({ ...p, hp: p.hp - dmg }));
          next = pushEvent(next, {
            type: "damage",
            sourceId,
            targetId,
            amount: dmg,
            damageType: "ss",
          });
        }
        break;
      }
      // 武器特效（攻击命中后触发）
      case "倚天剑":
        // 倚天剑：目标禁疗1回合
        next = addStatusWithEvent(
          next,
          targetId,
          createStatus("禁疗", sourceId, 1, { noHeal: true })
        );
        break;
      case "赤月鸟":
        // 赤月鸟：目标攻击力-10，持续1回合
        next = addStatusWithEvent(
          next,
          targetId,
          createStatus("赤月鸟减攻", sourceId, 1, { attackBonus: -10 })
        );
        break;
      case "霸者之刃":
        // 霸者之刃：目标防御-3，魔御-5，持续1回合
        next = addStatusWithEvent(
          next,
          targetId,
          createStatus("霸者减防", sourceId, 1, {
            defenseBonus: -3,
            magicDefenseBonus: -5,
          })
        );
        break;
      case "记忆之剑":
        // 记忆之剑：目标净化（消除一切状态）
        next = applyCleanse(next, targetId);
        break;
      // 幽魂（打出时加算）/ 血煞（打出时自损+加算）/ 恶魔（打出时加算+响应限制）/ 闪电（结算前弃牌）不在此处理
      default:
        break;
    }
  }
  return next;
}

// 战士刺杀剑术：攻击命中且目标下家是友军 → 下家抽目标 1 张手牌
function applyOnAttackHitSteal(
  state: GameState,
  source: PlayerState,
  target: PlayerState
): GameState {
  const passive = getPassive(source.hero.name);
  if (!passive.onAttackHitSteal) return state;
  if (target.hand.length === 0) return state;
  const n = state.players.length;
  let idx = (target.seat + 1) % n;
  let neighbor = state.players.find((p) => p.seat === idx);
  let guard = 0;
  while (neighbor && !neighbor.isAlive && guard < n) {
    idx = (idx + 1) % n;
    neighbor = state.players.find((p) => p.seat === idx);
    guard++;
  }
  if (!neighbor || !neighbor.isAlive) return state;
  if (neighbor.team !== source.team || neighbor.id === source.id) return state;
  const taken = target.hand[target.hand.length - 1];
  let next = updatePlayer(state, target.id, (p) => ({
    ...p,
    hand: p.hand.filter((c) => c.uid !== taken.uid),
  }));
  next = updatePlayer(next, neighbor.id, (p) => ({
    ...p,
    hand: [...p.hand, taken],
  }));
  next = pushEvent(next, {
    type: "transfer",
    fromId: target.id,
    toId: neighbor.id,
    cardId: taken.id,
    reason: "刺杀剑术",
  });
  return next;
}

// 恐惧之盾：若攻击造成伤害，目标拿起这张攻击牌（从弃牌堆移入手牌）
function applyFearShield(
  state: GameState,
  effect: PendingEffect,
  targetId: string,
  damageDealt: number
): GameState {
  const target = state.players.find((p) => p.id === targetId);
  if (!target) return state;
  if (target.equip.armor?.name !== "恐惧之盾") return state;
  if (damageDealt <= 0) return state;
  if (!effect.card) return state;
  const cardUid = effect.card.uid;
  const cardInDiscard = state.discardPile.find((c) => c.uid === cardUid);
  if (!cardInDiscard) return state;
  let next: GameState = {
    ...state,
    discardPile: state.discardPile.filter((c) => c.uid !== cardUid),
  };
  next = updatePlayer(next, targetId, (p) => ({
    ...p,
    hand: [...p.hand, cardInDiscard],
  }));
  next = pushEvent(next, {
    type: "transfer",
    fromId: effect.source,
    toId: targetId,
    cardId: cardInDiscard.id,
    reason: "恐惧之盾",
  });
  return next;
}

// 补给恢复结算（拒援已在上游处理；命运链接平摊治疗）
function applySupplyHeal(state: GameState, effect: PendingEffect): GameState {
  const tid = Array.isArray(effect.target) ? effect.target[0] : effect.target;
  return applyHealWithLink(state, tid, effect.healHp ?? 0, effect.healMp ?? 0);
}

// 治疗结算（命运链接平摊）
function applyHealWithLink(
  state: GameState,
  targetId: string,
  healHp: number,
  healMp: number
): GameState {
  const target = state.players.find((p) => p.id === targetId);
  if (!target) return state;
  const partnerId = fateLinkPartner(target);
  const partner =
    partnerId && partnerId !== targetId
      ? state.players.find((p) => p.id === partnerId)
      : undefined;
  if (partner && partner.isAlive && target.isAlive) {
    const hpEach = Math.floor(healHp / 2);
    const mpEach = Math.floor(healMp / 2);
    let next = healOne(state, targetId, hpEach, mpEach);
    next = healOne(next, partner.id, hpEach, mpEach);
    return next;
  }
  return healOne(state, targetId, healHp, healMp);
}

function healOne(
  state: GameState,
  playerId: string,
  hp: number,
  mp: number
): GameState {
  if (hp <= 0 && mp <= 0) return state;
  const target = state.players.find((p) => p.id === playerId)!;
  if (!target.isAlive) return state;
  // 禁疗（倚天剑）：无法恢复生命，MP 恢复不受影响
  const effectiveHp = isNoHeal(target) ? 0 : hp;
  if (effectiveHp <= 0 && mp <= 0) return state;
  const healed = applyHeal(target, effectiveHp, mp);
  let next = updatePlayer(state, playerId, (p) => ({ ...p, ...healed }));
  if (target.isDying && healed.hp > 0) {
    next = rescueByHeal(next, playerId);
  }
  return next;
}

// 净化：消除目标一切状态（公共法术牌"净化"与记忆之剑共用）
function applyCleanse(state: GameState, targetId: string): GameState {
  const target = state.players.find((p) => p.id === targetId);
  if (!target || target.statuses.length === 0) return state;
  const removed = target.statuses;
  let next = updatePlayer(state, targetId, (p) => ({ ...p, statuses: [] }));
  for (const st of removed) {
    next = pushEvent(next, {
      type: "status-remove",
      playerId: targetId,
      statusName: st.name,
    });
  }
  return next;
}

// 灭焰神戒：被缴械/掠夺/更换后立即恢复50点魔法（上限截断）
function restoreMpOnMieyanRingRemoved(
  state: GameState,
  targetId: string,
  removedCard: DeckCard
): GameState {
  if (removedCard.name !== "灭焰神戒") return state;
  const target = state.players.find((p) => p.id === targetId);
  if (!target) return state;
  const mp = Math.min(target.maxMp, target.mp + 50);
  if (mp <= target.mp) return state;
  const restored = mp - target.mp;
  let next = updatePlayer(state, targetId, (p) => ({ ...p, mp }));
  next = pushEvent(next, {
    type: "heal",
    sourceId: targetId,
    targetId,
    amount: restored,
    mp: true,
  });
  return next;
}

// 公共法术/效果牌操作结算（响应窗口结束后）
function applyOp(state: GameState, effect: PendingEffect): GameState {
  const op = effect.op!;
  const tid = Array.isArray(effect.target) ? effect.target[0] : effect.target;
  const target = state.players.find((p) => p.id === tid)!;
  let next = state;
  switch (op.kind) {
    case "steal-card": {
      if (target.hand.length === 0) return next;
      // 玩家从背面手牌中点选位置（看不到牌面）；缺省/指定牌已不在手牌 → 最后一张
      const specified = op.stealUid
        ? target.hand.find((c) => c.uid === op.stealUid)
        : undefined;
      const taken = specified ?? target.hand[target.hand.length - 1];
      next = updatePlayer(next, tid, (p) => ({
        ...p,
        hand: p.hand.filter((c) => c.uid !== taken.uid),
      }));
      next = updatePlayer(next, effect.source, (p) => ({
        ...p,
        hand: [...p.hand, taken],
      }));
      next = pushEvent(next, {
        type: "transfer",
        fromId: tid,
        toId: effect.source,
        cardId: taken.id,
        reason: "飞龙探云手",
      });
      return next;
    }
    case "cleanse": {
      return applyCleanse(next, tid);
    }
    case "discard-suit": {
      const suit = op.suit ?? null;
      const toDiscard = target.hand.filter((c) => c.suit === suit);
      if (toDiscard.length === 0) return next;
      next = updatePlayer(next, tid, (p) => ({
        ...p,
        hand: p.hand.filter((c) => c.suit !== suit),
      }));
      // 杀戮之心：被动弃牌，可被拾遗取回
      next = {
        ...next,
        discardPile: [
          ...next.discardPile,
          ...toDiscard.map((c) => ({ ...c, discardReason: "passive" as const })),
        ],
      };
      next = pushEvent(next, { type: "discard", playerId: tid, count: toDiscard.length });
      return next;
    }
    case "add-status": {
      const s = op.status!;
      return addStatusWithEvent(
        next,
        tid,
        createStatus(s.name, effect.source, s.turns, s.modifiers)
      );
    }
    case "unequip": {
      const slot = op.slot!;
      const eq = target.equip[slot];
      if (!eq) return next;
      // 走 unequip 助手：同步回收生命上限加成（魔血戒指）
      const { player: stripped } = doUnequip(
        next.players.find((p) => p.id === tid)!,
        slot
      );
      next = updatePlayer(next, tid, () => stripped);
      // 缴械：被动弃牌，可被拾遗取回
      next = {
        ...next,
        discardPile: [...next.discardPile, { ...eq, discardReason: "passive" }],
      };
      next = pushEvent(next, { type: "discard", playerId: tid, count: 1 });
      // 灭焰神戒：被缴械后恢复50点魔法
      next = restoreMpOnMieyanRingRemoved(next, tid, eq);
      return next;
    }
    case "steal-equip": {
      const slot = op.slot!;
      const eq = target.equip[slot];
      if (!eq) return next;
      // 受害者卸下装备：回收生命上限加成（魔血戒指）；夺走的牌进攻击者手牌（未装备，不加属性）
      const { player: stripped } = doUnequip(
        next.players.find((p) => p.id === tid)!,
        slot
      );
      next = updatePlayer(next, tid, () => stripped);
      next = updatePlayer(next, effect.source, (p) => ({
        ...p,
        hand: [...p.hand, eq],
      }));
      next = pushEvent(next, {
        type: "transfer",
        fromId: tid,
        toId: effect.source,
        cardId: eq.id,
        reason: "掠夺",
      });
      // 灭焰神戒：被掠夺后恢复50点魔法
      next = restoreMpOnMieyanRingRemoved(next, tid, eq);
      return next;
    }
    case "take-from-discard": {
      // 拾遗规则：只能取判定牌/被动弃牌（自己打出的、回合结束弃的不可取）
      const eligible = next.discardPile.filter(canPickFromDiscard);
      const uid = op.takeUid ?? eligible[eligible.length - 1]?.uid;
      const taken = next.discardPile.find((c) => c.uid === uid);
      if (!taken || !canPickFromDiscard(taken)) return next;
      next = {
        ...next,
        discardPile: next.discardPile.filter((c) => c.uid !== taken.uid),
      };
      next = updatePlayer(next, effect.source, (p) => ({
        ...p,
        hand: [...p.hand, taken],
      }));
      return next;
    }
    case "none":
      return next;
    default:
      return next;
  }
}

function updatePlayer(
  state: GameState,
  playerId: string,
  fn: (p: PlayerState) => PlayerState
): GameState {
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId ? fn(p) : p)),
  };
}

// 从手牌移除一张牌并加入弃牌堆
function spendCard(
  state: GameState,
  playerId: string,
  cardUid: number
): { state: GameState; card: DeckCard } {
  const player = state.players.find((p) => p.id === playerId)!;
  const card = player.hand.find((c) => c.uid === cardUid);
  if (!card) throw new Error(`玩家 ${playerId} 手中没有牌 uid=${cardUid}`);
  const nextState = updatePlayer(state, playerId, (p) => ({
    ...p,
    hand: p.hand.filter((c) => c.uid !== cardUid),
  }));
  return {
    // 自己打出的牌：拾遗不可取
    state: {
      ...nextState,
      discardPile: [...nextState.discardPile, { ...card, discardReason: "play" }],
    },
    card,
  };
}

// 虚拟牌感知消耗：uid=-1 时跳过手牌移除（替换模式已预先消耗2张手牌）
function spendCardOrVirtual(
  state: GameState,
  playerId: string,
  cardUid: number,
  card: DeckCard
): { state: GameState; card: DeckCard } {
  if (card.uid === -1) return { state, card };
  return spendCard(state, playerId, cardUid);
}

// ---------------- 卡牌替换（弃鬼盾/掠夺之甲/狂风刃甲/杀戮之戒/狂风项链） ----------------

// 查找玩家装备中的替换配置（返回装备名 + 配置）
export function findSubstituteConfig(
  player: PlayerState,
  trigger: "active" | "respond"
): { equipName: string; config: { cardName: string; trigger: string; suitFilter?: Suit } } | null {
  for (const slot of ["weapon", "armor", "accessory"] as const) {
    const eq = player.equip[slot];
    if (!eq) continue;
    const cfg = SUBSTITUTE_CONFIG[eq.name];
    if (cfg && cfg.trigger === trigger) {
      return { equipName: eq.name, config: cfg };
    }
  }
  return null;
}

// 替换配置：装备名 → { 替换为的牌名, 触发时机, 花色限制 }
const SUBSTITUTE_CONFIG: Record<string, {
  cardName: string;
  trigger: "active" | "respond";
  suitFilter?: Suit;
}> = {
  弃鬼盾: { cardName: "解除", trigger: "respond" },
  狂风刃甲: { cardName: "反射", trigger: "respond", suitFilter: "黑" },
  掠夺之甲: { cardName: "掠夺", trigger: "active" },
  杀戮之戒: { cardName: "PG攻击", trigger: "active" },
  狂风项链: { cardName: "HG攻击", trigger: "active" },
};

// 从 catalog 构造虚拟 DeckCard（uid=-1，不在手牌/牌堆中）
export function makeVirtualCard(state: GameState, cardName: string): DeckCard {
  const cat = getCatalogEntry(state, cardName);
  if (!cat) throw new Error(`catalog 中找不到: ${cardName}`);
  const template = cat.cards[0] ?? {};
  return {
    uid: -1,
    id: cat.id,
    name: cat.name,
    kind: cat.kind,
    subkind: cat.subkind,
    suit: template.suit,
    rank: template.rank,
    phase1: cat.phase1 ?? false,
    damageType: cat.damageType,
    responds: cat.responds,
    effectText: cat.effectText,
  };
}

// 消耗2张手牌（进弃牌堆），返回消耗的2张牌
function spendSubstituteCards(
  state: GameState,
  playerId: string,
  uids: [number, number]
): { state: GameState; cards: [DeckCard, DeckCard] } {
  const player = state.players.find((p) => p.id === playerId)!;
  const c1 = player.hand.find((c) => c.uid === uids[0]);
  const c2 = player.hand.find((c) => c.uid === uids[1]);
  if (!c1 || !c2) throw new Error(`玩家 ${playerId} 手中没有指定的替换牌`);
  if (uids[0] === uids[1]) throw new Error("替换牌不能是同一张");
  const uidSet = new Set(uids);
  const nextState: GameState = {
    ...updatePlayer(state, playerId, (p) => ({
      ...p,
      hand: p.hand.filter((c) => !uidSet.has(c.uid)),
    })),
    discardPile: [
      ...state.discardPile,
      { ...c1, discardReason: "play" },
      { ...c2, discardReason: "play" },
    ],
  };
  return { state: nextState, cards: [c1, c2] };
}

// 校验替换牌花色限制
function validateSubstituteSuit(
  cards: [DeckCard, DeckCard],
  suitFilter?: Suit
): void {
  if (!suitFilter) return;
  for (const c of cards) {
    if (c.suit !== suitFilter) {
      throw new Error(`替换牌需要 ${suitFilter} 色手牌`);
    }
  }
}

// ---------------- 出牌路由 ----------------

function playCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>
): GameState {
  const player = state.players.find((p) => p.id === action.playerId);
  if (!player) throw new Error(`玩家不存在: ${action.playerId}`);
  if (player.isDying) throw new Error("濒死状态无法出牌");

  // 卡牌替换模式（掠夺之甲/杀戮之戒/狂风项链）
  if (action.substituteCardUids && action.substituteAs) {
    const sub = findSubstituteConfig(player, "active");
    if (!sub) throw new Error("没有可用于主动替换的装备");
    if (sub.config.cardName !== action.substituteAs) {
      throw new Error(`装备 ${sub.equipName} 替换为 ${sub.config.cardName}，而非 ${action.substituteAs}`);
    }
    const { state: spentState, cards } = spendSubstituteCards(
      state,
      action.playerId,
      action.substituteCardUids
    );
    validateSubstituteSuit(cards, sub.config.suitFilter);
    const virtualCard = makeVirtualCard(spentState, action.substituteAs);
    validateCardPlay(player, virtualCard);
    const next = routePlayCard(spentState, action, virtualCard);
    return {
      ...next,
      turnFlags: { ...next.turnFlags, anyCardPlayed: true },
    };
  }

  const card = player.hand.find((c) => c.uid === action.cardUid);
  if (!card) throw new Error(`手中没有这张牌: uid=${action.cardUid}`);
  validateCardPlay(player, card);

  const next = routePlayCard(state, action, card);
  // 任意牌打出都记入回合标记（暗兵：只有整回合未打出任何牌才可跳过弃牌）
  return {
    ...next,
    turnFlags: { ...next.turnFlags, anyCardPlayed: true },
  };
}

function routePlayCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  switch (card.kind) {
    case "攻击牌":
      return playAttackCard(state, action, card);
    case "法术牌":
      return playSpellCard(state, action, card);
    case "摄魂牌":
      return playSoulCard(state, action, card);
    case "补给牌":
      return playSupplyCard(state, action, card);
    case "装备牌":
      return playEquipCard(state, action, card);
    case "公共法术牌":
      return playPublicSpellCard(state, action, card);
    case "效果牌":
      return playEffectCard(state, action, card);
    default:
      throw new Error(`暂不支持打出该类牌: ${card.name} (${card.kind})`);
  }
}

// 状态限制校验（寒冰/沉默/无间炼狱/禁攻）
function validateCardPlay(player: PlayerState, card: DeckCard): void {
  if (isFrozen(player)) {
    throw new Error("寒冰：无法打出任何手牌");
  }
  if (
    isSilenced(player) &&
    (card.kind === "法术牌" || card.kind === "公共法术牌")
  ) {
    throw new Error("沉默：无法使用法术牌");
  }
  if (isEffectLocked(player) && card.kind === "效果牌") {
    throw new Error("无间炼狱：无法打出效果牌");
  }
  if (isAttackLocked(player) && card.kind === "攻击牌") {
    throw new Error("禁攻：无法打出攻击牌");
  }
}

// 主行动互斥校验（嗜血/连刺例外）
function requireMainActionAvailable(
  state: GameState,
  playerId: string,
  kind: CardKind
): void {
  const player = state.players.find((p) => p.id === playerId)!;
  if (state.currentPlayerIdx !== state.players.findIndex((p) => p.id === playerId)) {
    throw new Error("不是该玩家的执行阶段");
  }
  if (state.phase !== "play") {
    throw new Error(`当前阶段不能出牌: ${state.phase}`);
  }
  if (player.hasPlayedAttackThisTurn) {
    if (hasBloodlust(player) && kind !== "摄魂牌") {
      return; // 嗜血：本执行阶段可出任意张攻击/法术
    }
    const passive = getPassive(player.hero.name);
    if (
      kind === "攻击牌" &&
      state.turnFlags.attacksPlayed < (passive.maxAttacksPerTurn ?? 1)
    ) {
      return; // 连刺：每回合可打 2 张攻击牌
    }
    // 狂风之刃：每个执行阶段可使用2张攻击/法术/摄魂牌
    if (
      player.equip.weapon?.name === "狂风之刃" &&
      state.turnFlags.attacksPlayed < 2
    ) {
      return;
    }
    // 诛神之剑：攻击牌未造成伤害时可再打出1张攻击牌
    if (
      kind === "攻击牌" &&
      player.equip.weapon?.name === "诛神之剑" &&
      state.turnFlags.zhushenComboAvailable
    ) {
      return;
    }
    throw new Error("每回合只能打出一张攻击/法术/摄魂牌");
  }
}

function playAttackCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  const catalog = getCatalogEntry(state, card.id);
  requireMainActionAvailable(state, action.playerId, "攻击牌");
  const targetId = requireTarget(action);
  if (!isTargetInAttackRange(state, action.playerId, targetId)) {
    throw new Error("目标不在攻击范围内");
  }
  const attacker = state.players.find((p) => p.id === action.playerId)!;
  const target = state.players.find((p) => p.id === targetId)!;
  if (!target.isAlive) throw new Error("目标已阵亡");
  // 龙鳞：无法成为红色攻击牌的目标
  assertTargetableByCard(state, targetId, card);
  // 呼神项链：可指定第2个目标（须在射程内、存活、不同于目标1）
  let target2: PlayerState | undefined;
  if (action.targetId2 && !hasHuShenNecklace(attacker)) {
    throw new Error("未装备呼神项链，不能指定第二目标");
  }
  if (action.targetId2 && hasHuShenNecklace(attacker)) {
    if (action.targetId2 === targetId) {
      throw new Error("两个目标不能相同");
    }
    if (!isTargetInAttackRange(state, action.playerId, action.targetId2)) {
      throw new Error("第二目标不在攻击范围内");
    }
    target2 = state.players.find((p) => p.id === action.targetId2)!;
    if (!target2.isAlive) throw new Error("第二目标已阵亡");
    assertTargetableByCard(state, action.targetId2, card);
  }
  const dmg = attackCardDamage(card, attacker);

  const passive = getPassive(attacker.hero.name);
  let base = dmg.base;
  let multiplier = dmg.multiplier;
  const extras: string[] = [];
  let ignoreDefense: number | undefined;

  // 狼行（弓箭手）：距离 >1 时伤害无视 5 防
  if (
    passive.rangeIgnoreDefense &&
    baseDistance(state.players, action.playerId, targetId) > 1
  ) {
    ignoreDefense = passive.rangeIgnoreDefense;
  }

  // TG附加效果
  const isTG = card.name.startsWith("TG攻击-");
  const extraName = isTG ? card.name.slice("TG攻击-".length) : null;
  if (extraName) {
    extras.push(extraName);
    if (extraName === "幽魂") base += attacker.hand.length + target.hand.length;
    if (extraName === "血煞") base += 15;
    if (extraName === "恶魔") base += 5;
  }

  // 武器特效
  const weaponName = attacker.equip.weapon?.name;
  if (weaponName === "开天") {
    // 开天：攻击伤害结算时无视目标4点防御（取最大值，与狼行叠加取较高）
    ignoreDefense = Math.max(ignoreDefense ?? 0, 4);
  }
  // 倚天剑/赤月鸟/霸者之刃/记忆之剑：攻击命中后附加效果（与 TG 附加同时触发）
  if (weaponName === "倚天剑") extras.push("倚天剑");
  if (weaponName === "赤月鸟") extras.push("赤月鸟");
  if (weaponName === "霸者之刃") extras.push("霸者之刃");
  if (weaponName === "记忆之剑") extras.push("记忆之剑");

  // 暴击（巨剑骑士）：20% 双倍；暴击后再 20% 追加 20ss
  let nextSeed = state.seed;
  let critTriggered = false;
  let critMultiplier = 1;
  if (passive.critChance) {
    const rng = new Rng(state.seed);
    if (rng.next() < passive.critChance) {
      multiplier *= 2;
      critTriggered = true;
      critMultiplier = 2;
      if (rng.next() < (passive.critFollowChance ?? 0)) extras.push("暴击追加");
    }
    nextSeed = rng.snapshot();
  }

  const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
  let next: GameState = {
    ...spent.state,
    seed: nextSeed,
    players: spent.state.players.map((p) =>
      p.id === action.playerId
        ? { ...p, hasPlayedAttackThisTurn: true }
        : p
    ),
    turnFlags: {
      ...spent.state.turnFlags,
      attacksPlayed: spent.state.turnFlags.attacksPlayed + 1,
      // 诛神之剑连击：消耗连击机会（打出第二张攻击时清除标记）
      zhushenComboAvailable: false,
    },
  };
  // 血煞：攻击者损失生命 10（ss，不可致死）
  if (extraName === "血煞") {
    next = updatePlayer(next, action.playerId, (p) => ({
      ...p,
      hp: Math.max(1, p.hp - 10),
    }));
    next = pushEvent(next, {
      type: "damage",
      sourceId: action.playerId,
      targetId: action.playerId,
      amount: 10,
      damageType: "ss",
    });
  }
  next = pushEvent(next, {
    type: "play",
    playerId: action.playerId,
    cardId: card.id,
    targetId,
  });
  // 暴击事件：日志+UI 特效（金色爆炸）
  if (critTriggered) {
    next = pushEvent(next, {
      type: "crit",
      sourceId: action.playerId,
      targetId,
      multiplier: critMultiplier,
    });
  }
  const effect = makePendingEffect({
    source: action.playerId,
    // 呼神项链：两个目标依次结算（共用一个多目标响应窗口）
    target: target2 ? [targetId, target2.id] : targetId,
    card: spent.card,
    damageType: dmg.type,
    damageAmount: base,
    multiplier,
    ignoreDefense,
    attackExtras: extras.length > 0 ? extras : undefined,
    // 沃玛之翅：攻击/法术无法被反射
    allowResponses: (catalog?.responds ?? []).filter(
      (k) => !(attacker.equip.armor?.name === "沃玛之翅" && k === "反射")
    ),
  });
  return openWindowOrResolve(next, effect);
}

function playSpellCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  requireMainActionAvailable(state, action.playerId, "法术牌");
  const player = state.players.find((p) => p.id === action.playerId)!;
  // 法术牌 = 执行英雄主动技能
  const skill = player.hero.skills.find(
    (s) => s.type === "active" && (!action.skillName || s.name === action.skillName)
  );
  if (!skill) {
    throw new Error(
      action.skillName
        ? `技能【${action.skillName}】不是主动技能或不存在`
        : "需要指定要执行的英雄技能 (skillName)"
    );
  }
  // 法神戒指：法术魔法消耗-5（不低于0）
  const manaCost = Math.max(
    0,
    (skill.manaCost ?? 0) - equipManaCostReduction(player)
  );
  if (player.mp < manaCost) {
    throw new Error(`魔法不足: 需要 ${manaCost}`);
  }
  const def = getActiveSkill(skill.name);
  const dmg = parseSkillDamage(skill.description);
  const catalog = getCatalogEntry(state, card.id);
  // 圣言术不可反射 → 过滤反射；沃玛之翅：攻击/法术无法被反射
  const baseResponses = (catalog?.responds ?? []).filter(
    (k) =>
      !(def?.excludeReflect && k === "反射") &&
      !(player.equip.armor?.name === "沃玛之翅" && k === "反射")
  );

  const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
  let next: GameState = {
    ...spent.state,
    players: spent.state.players.map((p) =>
      p.id === action.playerId
        ? {
            ...p,
            hasPlayedAttackThisTurn: true,
            hasUsedSkillThisTurn: true,
            mp: p.mp - manaCost,
          }
        : p
    ),
    turnFlags: {
      ...spent.state.turnFlags,
      attacksPlayed: spent.state.turnFlags.attacksPlayed + 1,
    },
  };
  next = pushEvent(next, {
    type: "play",
    playerId: action.playerId,
    cardId: card.id,
    targetId: action.targetId,
  });
  next = pushEvent(next, {
    type: "skill",
    playerId: action.playerId,
    skillName: skill.name,
    targetId: action.targetId,
  });

  // 天使降临：全友军恢复（fz 直结）
  if (def?.targetMode === "all-allies") {
    for (const ally of next.players.filter(
      (p) => p.team === player.team && p.isAlive
    )) {
      next = applyHealWithLink(next, ally.id, def.heal ?? 0, 0);
    }
    return next;
  }

  // 救赎：友军 → 恢复（直结）；敌军 → 伤害（可响应）
  if (def?.targetMode === "redeem") {
    const targetId = requireTarget(action);
    const target = next.players.find((p) => p.id === targetId)!;
    if (!target.isAlive) throw new Error("目标已阵亡");
    if (target.team === player.team) {
      return applyHealWithLink(next, targetId, def.heal ?? 0, 0);
    }
    // 凤羽：无法成为蓝色法术牌的目标
    assertTargetableByCard(next, targetId, card);
    const effect = makePendingEffect({
      source: action.playerId,
      target: targetId,
      card: spent.card,
      skill,
      damageType: "f",
      damageAmount: dmg?.amount ?? 0,
      allowResponses: baseResponses,
    });
    return openWindowOrResolve(next, effect);
  }

  // 乱符千吻：对所有敌方（多目标响应窗口）
  if (def?.targetMode === "all-enemies") {
    const targets = next.players
      .filter((p) => p.team !== player.team && p.isAlive)
      // 凤羽：蓝色法术牌自动跳过佩戴者
      .filter((p) => !accessoryTargetBlockReason(p, card))
      .map((p) => p.id);
    if (targets.length === 0) throw new Error("没有可指定的敌方目标");
    const effect = makePendingEffect({
      source: action.playerId,
      target: targets,
      card: spent.card,
      skill,
      damageType: "sf",
      damageAmount: dmg?.amount ?? 0,
      allowResponses: baseResponses,
    });
    return openWindowOrResolve(next, effect);
  }

  // 单体伤害技能
  if (!dmg) {
    throw new Error(`技能【${skill.name}】没有可解析的伤害数值`);
  }
  const targetId = requireTarget(action);
  const target = next.players.find((p) => p.id === targetId)!;
  if (!target.isAlive) throw new Error("目标已阵亡");
  // 凤羽：无法成为蓝色法术牌的目标
  assertTargetableByCard(next, targetId, card);
  // S法术: 技能伤害变为 sf（无视魔御）
  const dtype = card.damageType === "sf" ? "sf" : dmg.dtype;
  const effect = makePendingEffect({
    source: action.playerId,
    target: targetId,
    card: spent.card,
    skill,
    damageType: dtype,
    damageAmount: dmg.amount,
    skillStatuses: def?.statuses,
    allowResponses: baseResponses,
  });
  return openWindowOrResolve(next, effect);
}

function playSoulCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  requireMainActionAvailable(state, action.playerId, "摄魂牌");
  const targetId = requireTarget(action);
  const target = state.players.find((p) => p.id === targetId)!;
  if (!target.isAlive) throw new Error("目标已阵亡");
  const catalog = getCatalogEntry(state, card.id);
  const amount = parseSoulAmount(catalog?.effectText ?? "");
  if (amount <= 0) throw new Error(`无法解析摄魂牌数值: ${card.name}`);
  const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
  let next: GameState = {
    ...spent.state,
    players: spent.state.players.map((p) =>
      p.id === action.playerId
        ? { ...p, hasPlayedAttackThisTurn: true }
        : p
    ),
    turnFlags: {
      ...spent.state.turnFlags,
      attacksPlayed: spent.state.turnFlags.attacksPlayed + 1,
    },
  };
  next = pushEvent(next, {
    type: "play",
    playerId: action.playerId,
    cardId: card.id,
    targetId,
  });
  const effect = makePendingEffect({
    source: action.playerId,
    target: targetId,
    card: spent.card,
    damageType: card.damageType ?? "ss",
    damageAmount: amount,
    allowResponses: catalog?.responds ?? [],
  });
  return openWindowOrResolve(next, effect);
}

// 补给牌：给自己或队友使用；任意活人在 play 阶段可用
function playSupplyCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  if (state.phase !== "play") {
    throw new Error(`当前阶段不能使用补给: ${state.phase}`);
  }
  const player = state.players.find((p) => p.id === action.playerId)!;
  if (!player.isAlive) throw new Error("阵亡玩家不能使用补给");
  const catalog = getCatalogEntry(state, card.id);
  const heal = catalog ? parseSupplyHeal(catalog.effectText) : {};
  if (!heal.hp && !heal.mp) {
    // 金币等无恢复效果的补给牌：直接弃置
    const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
    return pushEvent(spent.state, {
      type: "play",
      playerId: action.playerId,
      cardId: card.id,
    });
  }
  const targetId = action.targetId ?? action.playerId;
  const target = state.players.find((p) => p.id === targetId)!;
  if (target.team !== player.team) {
    throw new Error("补给只能给自己或队友");
  }
  const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
  let next: GameState = spent.state;
  next = pushEvent(next, {
    type: "play",
    playerId: action.playerId,
    cardId: card.id,
    targetId,
  });
  const effect = makePendingEffect({
    source: action.playerId,
    target: targetId,
    card: spent.card,
    // 记忆之盾：补给牌无法被拒援
    allowResponses:
      player.equip.armor?.name === "记忆之盾" ? [] : ["拒援"],
    healHp: heal.hp,
    healMp: heal.mp,
  });
  return openWindowOrResolve(next, effect);
}

function playEquipCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  const player = state.players.find((p) => p.id === action.playerId)!;
  if (state.phase !== "play" || state.currentPlayerIdx !== state.players.findIndex((p) => p.id === action.playerId)) {
    throw new Error("装备只能在执行阶段使用");
  }
  const slot = equipSlotOf(card);
  if (!slot) throw new Error(`无法识别装备位置: ${card.name}`);
  const catalog = getCatalogEntry(state, card.id);
  if (!catalog) throw new Error(`目录中找不到: ${card.id}`);
  const { player: equipped, oldCard, healApplied } = doEquip(player, card, catalog);
  // 弃掉旧装备 + 打出的装备牌离开手牌（进入装备槽，不进弃牌堆）
  let next = updatePlayer(state, action.playerId, () => ({
    ...equipped,
    hand: equipped.hand.filter((c) => c.uid !== action.cardUid),
  }));
  if (oldCard) {
    // 被替换下来的旧装备：被动弃牌，可被拾遗取回
    next = {
      ...next,
      discardPile: [...next.discardPile, { ...oldCard, discardReason: "passive" }],
    };
    // 灭焰神戒：被更换后立即恢复50点魔法
    next = restoreMpOnMieyanRingRemoved(next, action.playerId, oldCard);
  }
  next = pushEvent(next, {
    type: "play",
    playerId: action.playerId,
    cardId: card.id,
  });
  if (healApplied > 0) {
    // 魔血戒指：装备时立即恢复生命
    next = pushEvent(next, {
      type: "heal",
      sourceId: action.playerId,
      targetId: action.playerId,
      amount: healApplied,
    });
  }
  next = pushEvent(next, {
    type: "equip",
    playerId: action.playerId,
    slot,
    cardId: card.id,
  });
  // 替换装备产生被动弃牌 → 开拾遗响应窗口
  return maybeOpenPickup(next, oldCard ? [{ ...oldCard, discardReason: "passive" as const }] : [], action.playerId, "装备替换");
}

// ---------------- 公共法术牌 ----------------

// 定向：飞龙探云手/净化/杀戮之心/致命弱点/无间炼狱/沉默（可被虚灵/无敌响应）
// fz 非指向：贪婪/嗜血/命运链接（直接结算）
function playPublicSpellCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  if (state.phase !== "play") {
    throw new Error(`当前阶段不能出牌: ${state.phase}`);
  }
  // 公共法术牌不占攻/法/摄魂互斥
  const playerId = action.playerId;

  switch (card.name) {
    case "贪婪": {
      // 立即摸 2 张
      const spent = spendCardOrVirtual(state, playerId, action.cardUid, card);
      let next: GameState = pushEvent(spent.state, {
        type: "play",
        playerId,
        cardId: card.id,
      });
      const drawn = next.drawPile.slice(0, 2);
      next = { ...next, drawPile: next.drawPile.slice(2) };
      next = updatePlayer(next, playerId, (p) => ({
        ...p,
        hand: [...p.hand, ...drawn],
      }));
      next = pushEvent(next, { type: "draw", playerId, count: drawn.length });
      return next;
    }
    case "嗜血": {
      // 失去 10 点生命（ss，不可致死）+ 本执行阶段可出任意张攻击/法术
      const spent = spendCardOrVirtual(state, playerId, action.cardUid, card);
      let next: GameState = pushEvent(spent.state, {
        type: "play",
        playerId,
        cardId: card.id,
      });
      next = updatePlayer(next, playerId, (p) => ({
        ...p,
        hp: Math.max(1, p.hp - 10),
      }));
      next = pushEvent(next, {
        type: "damage",
        sourceId: playerId,
        targetId: playerId,
        amount: 10,
        damageType: "ss",
      });
      next = addStatusWithEvent(
        next,
        playerId,
        createStatus("嗜血", playerId, 1, { bloodlust: true })
      );
      return next;
    }
    case "命运链接": {
      // 指定 2 名相邻存活玩家平摊伤害/治疗 2 回合（可以不含使用者）
      const t1 = action.targetId ?? nextAliveClockwise(state, playerId);
      const t2 = action.targetId2 ?? nextAliveClockwise(state, t1);
      const p1 = state.players.find((p) => p.id === t1);
      const p2 = state.players.find((p) => p.id === t2);
      if (!p1 || !p2 || !p1.isAlive || !p2.isAlive) throw new Error("命运链接目标无效或已阵亡");
      if (t1 === t2) throw new Error("命运链接需要两个不同目标");
      const spent = spendCardOrVirtual(state, playerId, action.cardUid, card);
      let next: GameState = pushEvent(spent.state, {
        type: "play",
        playerId,
        cardId: card.id,
      });
      next = addStatusWithEvent(next, t1, makeLinkStatus(t1, t2));
      next = addStatusWithEvent(next, t2, makeLinkStatus(t2, t1));
      return next;
    }
  }

  // 定向公共法术
  const targetId = requireTarget(action);
  const target = state.players.find((p) => p.id === targetId)!;
  if (!target.isAlive) throw new Error("目标已阵亡");

  let op: EffectOp;
  switch (card.name) {
    case "飞龙探云手":
      if (target.hand.length === 0) throw new Error("目标没有手牌");
      op = { kind: "steal-card", stealUid: action.stealCardUid };
      break;
    case "净化":
      op = { kind: "cleanse" };
      break;
    case "杀戮之心":
      op = { kind: "discard-suit", suit: action.suit ?? majoritySuit(target) };
      break;
    case "致命弱点":
      op = {
        kind: "add-status",
        status: { name: "致命弱点", turns: 1, modifiers: {} },
      };
      break;
    case "无间炼狱":
      op = {
        kind: "add-status",
        status: { name: "无间炼狱", turns: 1, modifiers: { effectLocked: true } },
      };
      break;
    case "沉默":
      op = {
        kind: "add-status",
        status: { name: "沉默", turns: 1, modifiers: { silenced: true } },
      };
      break;
    default:
      throw new Error(`未实现的公共法术牌: ${card.name}`);
  }

  const spent = spendCardOrVirtual(state, playerId, action.cardUid, card);
  let next: GameState = pushEvent(spent.state, {
    type: "play",
    playerId,
    cardId: card.id,
    targetId,
  });
  const effect = makePendingEffect({
    source: playerId,
    target: targetId,
    card: spent.card,
    op,
    allowResponses: ["虚灵", "无敌"],
  });
  return openWindowOrResolve(next, effect);
}

// ---------------- 效果牌（主动类） ----------------

// 缴械/掠夺/拾遗/封杀；响应类（闪避/虚灵/反射/无敌/解除/拒援）走响应窗口
function playEffectCard(
  state: GameState,
  action: Extract<EngineAction, { type: "play-card" }>,
  card: DeckCard
): GameState {
  // 拾遗：无限制（P2 简化为 play 阶段可用），不占主行动
  if (card.name === "拾遗") {
    if (state.phase !== "play") {
      throw new Error(`当前阶段不能使用拾遗: ${state.phase}`);
    }
    // 拾遗规则：只能取判定牌/被动弃牌（自己打出的、回合结束弃的不可取）
    const eligible = state.discardPile.filter(canPickFromDiscard);
    const uid =
      action.takeFromDiscardUid ?? eligible[eligible.length - 1]?.uid;
    const taken = state.discardPile.find((c) => c.uid === uid);
    if (!taken) throw new Error("弃牌堆中没有指定的牌");
    if (!canPickFromDiscard(taken)) {
      throw new Error("该牌不可被拾遗（仅判定牌/被动弃牌可取）");
    }
    const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
    let next: GameState = {
      ...spent.state,
      discardPile: spent.state.discardPile.filter((c) => c.uid !== taken.uid),
    };
    next = updatePlayer(next, action.playerId, (p) => ({
      ...p,
      hand: [...p.hand, taken],
    }));
    next = pushEvent(next, {
      type: "play",
      playerId: action.playerId,
      cardId: card.id,
    });
    next = pushEvent(next, {
      type: "transfer",
      fromId: "弃牌堆",
      toId: action.playerId,
      cardId: taken.id,
      reason: "拾遗",
    });
    return next;
  }

  // 其余效果牌：回合内（当前玩家、play 阶段），不占主行动
  if (state.phase !== "play") {
    throw new Error(`当前阶段不能出效果牌: ${state.phase}`);
  }
  if (
    state.currentPlayerIdx !==
    state.players.findIndex((p) => p.id === action.playerId)
  ) {
    throw new Error("不是该玩家的执行阶段");
  }

  switch (card.name) {
    case "缴械":
    case "掠夺": {
      const targetId = requireTarget(action);
      const target = state.players.find((p) => p.id === targetId)!;
      if (!target.isAlive) throw new Error("目标已阵亡");
      // 掠夺之甲：无法成为掠夺的目标
      if (card.name === "掠夺" && target.equip.armor?.name === "掠夺之甲") {
        throw new Error("目标装备了掠夺之甲，无法掠夺");
      }
      const slot = action.slot ?? firstEquipSlot(target);
      if (!slot || !target.equip[slot]) throw new Error("目标没有可指定的装备");
      // 金刚戒指：除戒指自身（饰品槽）外的装备无法被缴械或掠夺
      if (isSlotProtectedByJingang(target, slot)) {
        throw new Error("金刚戒指：该装备无法被缴械或掠夺");
      }
      const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
      let next: GameState = pushEvent(spent.state, {
        type: "play",
        playerId: action.playerId,
        cardId: card.id,
        targetId,
      });
      // 记忆之盾：无法成为解除的目标（解除响应被过滤，仅留无敌）
      const allowResponses: ResponseKind[] =
        target.equip.armor?.name === "记忆之盾"
          ? ["无敌"]
          : ["解除", "无敌"];
      const effect = makePendingEffect({
        source: action.playerId,
        target: targetId,
        card: spent.card,
        op: { kind: card.name === "缴械" ? "unequip" : "steal-equip", slot },
        allowResponses,
      });
      return openWindowOrResolve(next, effect);
    }
    case "封杀": {
      // 解除目标任务区的1个任务 —— 一期任务系统未实现，no-op 弃置
      const spent = spendCardOrVirtual(state, action.playerId, action.cardUid, card);
      return pushEvent(spent.state, {
        type: "play",
        playerId: action.playerId,
        cardId: card.id,
      });
    }
    default:
      throw new Error(`未实现的效果牌: ${card.name}`);
  }
}

// ---------------- 依赖注入 ----------------

// P1 通过模块级 catalog 查询；loader 加载后由 setCatalog 注册
let CATALOG: CardCatalogEntry[] = [];
export function setCatalog(catalog: CardCatalogEntry[]): void {
  CATALOG = catalog;
}
export function getCatalogEntry(state: GameState, id: string): CardCatalogEntry | undefined {
  void state;
  return CATALOG.find((c) => c.id === id);
}

// ---------------- 校验工具 ----------------

function requireTarget(action: Extract<EngineAction, { type: "play-card" }>): string {
  if (!action.targetId) throw new Error("需要指定目标");
  return action.targetId;
}

function isTargetInAttackRange(
  state: GameState,
  attackerId: string,
  targetId: string
): boolean {
  return inRange(state.players, attackerId, targetId);
}

// 饰品目标免疫：返回阻止原因（null=可指定）
//   凤羽：无法成为蓝色法术牌的目标
//   龙鳞：无法成为红色攻击牌的目标
export function accessoryTargetBlockReason(
  target: PlayerState,
  card: DeckCard
): string | null {
  const acc = target.equip.accessory?.name;
  if (card.kind === "法术牌" && card.suit === "蓝" && acc === "凤羽") {
    return "凤羽：无法成为蓝色法术牌的目标";
  }
  if (card.kind === "攻击牌" && card.suit === "红" && acc === "龙鳞") {
    return "龙鳞：无法成为红色攻击牌的目标";
  }
  return null;
}

function assertTargetableByCard(
  state: GameState,
  targetId: string,
  card: DeckCard
): void {
  const target = state.players.find((p) => p.id === targetId);
  if (!target) throw new Error("目标不存在");
  const reason = accessoryTargetBlockReason(target, card);
  if (reason) throw new Error(reason);
}

// 呼神项链：攻击可指定 2 个目标
function hasHuShenNecklace(player: PlayerState): boolean {
  return player.equip.accessory?.name === "呼神项链";
}

// fz 效果：直接结算，跳过响应窗口
function openWindowOrResolve(state: GameState, effect: PendingEffect): GameState {
  // 地狱魔衣/绝望之翅：判定闪避/虚灵（在开窗前触发）
  const beforeJudgeUids = new Set(state.discardPile.map((c) => c.uid));
  const judged = tryArmorJudgment(state, effect);
  // 判定牌一进弃牌堆，立即开拾遗窗口；窗口关闭后再继续判定后的原效果流程
  const judgmentCards = judged.state.discardPile.filter(
    (c) =>
      !beforeJudgeUids.has(c.uid) && c.discardReason === "judgment"
  );
  if (judgmentCards.length > 0) {
    const victimId = Array.isArray(effect.target)
      ? effect.target[0]
      : effect.target;
    const withPickup = maybeOpenPickup(
      judged.state,
      judgmentCards,
      victimId,
      "判定",
      { effect, cancelled: judged.cancelled }
    );
    // 已开窗（有人持拾遗）→ 窗口关闭后由 handlePickup 调用 continueAfterJudgment
    if (withPickup.phase === "pickup-response") return withPickup;
  }
  return continueAfterJudgment(judged.state, effect, judged.cancelled);
}

// 判定（或判定拾遗窗口）结束后继续原效果流程
function continueAfterJudgment(
  state: GameState,
  effect: PendingEffect,
  cancelled: boolean
): GameState {
  if (cancelled) {
    // 诛神之剑：判定闪避/虚灵也算攻击未造成伤害
    let next = state;
    const src = next.players.find((p) => p.id === effect.source);
    if (
      effect.card?.kind === "攻击牌" &&
      src?.equip.weapon?.name === "诛神之剑" &&
      next.turnFlags.attacksPlayed === 1
    ) {
      next = {
        ...next,
        turnFlags: { ...next.turnFlags, zhushenComboAvailable: true },
      };
    }
    return next; // 判定成功：效果取消，回到 play
  }

  if (effect.nonTargetable) {
    return resolveEffect({ ...state, phase: "play" }, effect);
  }
  const withWindow = openResponseWindow(state, effect);
  // 无响应者（如对方全灭时补给）：跳过窗口直接结算
  if (withWindow.responseWindow && withWindow.responseWindow.responders.length === 0) {
    return resolveEffect({ ...withWindow, phase: "play", responseWindow: null }, effect);
  }
  return withWindow;
}

// 防具判定（地狱魔衣/绝望之翅）：受到攻击/法术时摸牌判定，色匹配=自动闪避/虚灵
// 判定牌进弃牌堆（discardReason=judgment），可被拾遗取回
function tryArmorJudgment(
  state: GameState,
  effect: PendingEffect
): { state: GameState; cancelled: boolean } {
  if (!effect.damageType) return { state, cancelled: false };
  const tid = Array.isArray(effect.target) ? effect.target[0] : effect.target;
  const target = state.players.find((p) => p.id === tid);
  if (!target) return { state, cancelled: false };

  // 地狱魔衣：受到攻击（w/hw）时判定红牌=闪避
  // 绝望之翅：受到法术（f/sf）时判定蓝牌=虚灵
  let armorName: string | undefined;
  let desiredSuit: Suit | undefined;
  let responseKind: ResponseKind | undefined;
  if (effect.damageType === "w" || effect.damageType === "hw") {
    if (target.equip.armor?.name === "地狱魔衣") {
      armorName = "地狱魔衣";
      desiredSuit = "红";
      responseKind = "闪避";
    }
  } else if (effect.damageType === "f" || effect.damageType === "sf") {
    if (target.equip.armor?.name === "绝望之翅") {
      armorName = "绝望之翅";
      desiredSuit = "蓝";
      responseKind = "虚灵";
    }
  }
  if (!armorName || !desiredSuit || !responseKind) {
    return { state, cancelled: false };
  }

  // 摸牌判定
  if (state.drawPile.length === 0) return { state, cancelled: false };
  const judgeCard = state.drawPile[0];
  let next: GameState = {
    ...state,
    drawPile: state.drawPile.slice(1),
    discardPile: [...state.discardPile, { ...judgeCard, discardReason: "judgment" as const }],
  };
  const success = judgeCard.suit === desiredSuit;
  next = pushEvent(next, {
    type: "judgment",
    playerId: tid,
    cardName: judgeCard.name,
    result: judgeCard.suit ?? "?",
    armorName,
    success,
    responseKind,
  });

  // 判定成功 → 自动闪避/虚灵，取消效果
  if (success) {
    next = pushEvent(next, {
      type: "response",
      playerId: tid,
      kind: responseKind,
      cardId: judgeCard.id,
    });
    return { state: next, cancelled: true };
  }
  return { state: next, cancelled: false };
}

// 顺时针下一个活人
function nextAliveClockwise(state: GameState, fromId: string): string {
  const from = state.players.find((p) => p.id === fromId)!;
  const n = state.players.length;
  let idx = (from.seat + 1) % n;
  let guard = 0;
  while (guard < n) {
    const candidate = state.players.find((p) => p.seat === idx)!;
    if (candidate.isAlive && candidate.id !== fromId) return candidate.id;
    idx = (idx + 1) % n;
    guard++;
  }
  throw new Error("没有相邻的玩家");
}

function makeLinkStatus(owner: string, partner: string): StatusInstance {
  const st = createStatus("命运链接", owner, 2, {});
  // 链接双方都跳过第一次释放者回合结束的 tick（保证同步消失）
  return { ...st, linkedTo: partner, justAppliedCrossPlayer: true };
}

// 杀戮之心未指定花色时：弃目标手牌中最多的花色
function majoritySuit(target: PlayerState): Suit {
  const counts: Record<string, number> = {};
  for (const c of target.hand) {
    if (c.suit) counts[c.suit] = (counts[c.suit] ?? 0) + 1;
  }
  let best: Suit = null;
  let bestN = 0;
  for (const [suit, n] of Object.entries(counts)) {
    if (n > bestN) {
      best = suit as Suit;
      bestN = n;
    }
  }
  return best;
}

function firstEquipSlot(p: PlayerState): EquipSlot | undefined {
  if (p.equip.weapon) return "weapon";
  if (p.equip.armor) return "armor";
  if (p.equip.accessory) return "accessory";
  return undefined;
}
